/**
 * importSummaryStore — useSyncExternalStore-compatible invalidation store.
 *
 * Tracks a monotonically incrementing version counter. Any hook subscribed
 * via useSyncExternalStore will re-render (and therefore re-fetch) when
 * importSummaryStore.invalidate() is called.
 *
 * Usage:
 *   // In a hook:
 *   const version = useSyncExternalStore(
 *     importSummaryStore.subscribe,
 *     importSummaryStore.getSnapshot
 *   );
 *   useEffect(() => { fetchSummary(year); }, [year, version, fetchSummary]);
 *
 *   // After a successful import:
 *   importSummaryStore.invalidate();
 */

type Listener = () => void;

let version = 0;
const listeners = new Set<Listener>();

export const importSummaryStore = {
  subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },

  getSnapshot(): number {
    return version;
  },

  invalidate(): void {
    version += 1;
    listeners.forEach((l) => l());
  },

  /** For testing: reset version to 0 and clear listeners. */
  _reset(): void {
    version = 0;
    listeners.clear();
  },
};
